import { Switch, Match } from "@9amhealth/shared";
import { useBlocNext } from "@blac/react";
import styled from "@emotion/styled";
import type { FC } from "react";
import { useEffect, useMemo } from "react";
import LabStatusOverview from "../LabStatusOverview/LabStatusOverview";
import LabStatusOverviewCubit from "../LabStatusOverview/LabStatusOverviewCubit";
import InitialLabsList from "./InitialLabsList";
import InitialLabsBloc from "./InitialLabsBloc";
import Track from "../Track/Track";
import FileUploadLabReport from "../FileUploadLabReport/FileUploadLabReport";

const Container = styled.div`
  label: InitialLabsFlowStatusStep;
  width: min(100%, calc(400px + var(--space-md) * 2));
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  padding: var(--space-md);
  text-align: left;
`;

export type LabsDisplayMode = "none" | "missing-all" | "missing-some-required";

const StatusStep: FC = () => {
  const [initialLabsState, { setUploadReady, setUploadedFileIds }] =
    useBlocNext(InitialLabsBloc);
  const [
    labStatusState,
    {
      fetchLabStatus,
      labsTrackingData,
      hasNoLabsCompleted,
      hasIncompleteRequiredLabs,
      allLabs
    }
  ] = useBlocNext(LabStatusOverviewCubit);

  useEffect(() => {
    void fetchLabStatus();
  }, []);

  const labsDisplayMode = useMemo<LabsDisplayMode>(() => {
    // Don't show if disabled or still loading/no data
    if (!initialLabsState.showLabStatusOverview) {
      return "none";
    }

    if (
      labStatusState.labData === null ||
      labStatusState.groupedCategories.length === 0
    ) {
      return "none";
    }

    if (hasNoLabsCompleted) {
      return "missing-all";
    }

    return "missing-some-required";
  }, [
    initialLabsState.showLabStatusOverview,
    labStatusState.labData,
    labStatusState.groupedCategories.length,
    hasNoLabsCompleted,
    hasIncompleteRequiredLabs
  ]);

  const trackingData = {
    ...initialLabsState,
    labsDisplayMode,
    ...labsTrackingData
  };

  return (
    <Container>
      <Track event="Initial Labs Dialog Status Shown" data={trackingData} />
      <Switch>
        <Match when={labsDisplayMode === "missing-all"}>
          <FileUploadLabReport
            onFileIdsChange={setUploadedFileIds}
            onReadyStateChange={setUploadReady}
          />
          <div style={{ height: "var(--space-xl)" }} />
          <LabStatusOverview
            displayMode="listOnly"
            groupedCategories={labStatusState.groupedCategories}
          />
        </Match>
        <Match when={labsDisplayMode === "missing-some-required"}>
          <InitialLabsList allLabs={allLabs} />
        </Match>
      </Switch>
    </Container>
  );
};

export default StatusStep;
